import React, { useState, useEffect } from 'react';
import axios from 'axios';
import MappedInDirectionsButton from './MappedInDirectionsButton';

const TextDirectionManager = ({ userId, locationId, currentTextDirections, onTextDirectionsUpdate, spaceName, buttonColor, currentLanguage, videoStatus,
  setVideoStatus }) => {
  const [textDirections, setTextDirections] = useState(currentTextDirections || '');
  const [displayText, setDisplayText] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateResult, setUpdateResult] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [hasCustomText, setHasCustomText] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchVideoStatus = async () => {
    if (!userId || !locationId || !currentLanguage) return;

    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(
        `/api/wayfinder/locations/${locationId}/video-status?language=${currentLanguage}`
      );

      if (response.data.success) {
        // Find the video status for the current language
        const currentVideoStatus = response.data.data.find(
          status => status.language === currentLanguage
        );
        setVideoStatus(currentVideoStatus || null);
      } else {
        setError('Failed to fetch video status');
      }
    } catch (err) {
      console.error('Error fetching video status:', err);
      setError(err.response?.data?.message || 'Failed to fetch video status');
    } finally {
      setLoading(false);
    }
  };

  // Update local state when prop changes
  useEffect(() => {
    setTextDirections(currentTextDirections || '');
    setDisplayText(currentTextDirections || ''); // Show SSML tags in textarea
    setHasChanges(false);
    // Check if current text is custom (not auto-generated from MappedIn)
    setHasCustomText(!!currentTextDirections && !currentTextDirections.includes('Welcome! Here are the directions to'));
  }, [currentTextDirections]);

  // Auto-fetch MappedIn directions when spaceName changes (only if no custom text exists)
  useEffect(() => {
    if (spaceName && !hasCustomText && !currentTextDirections) {
      // Auto-fetch will be handled by the button component when user clicks
      console.log('Space name available for auto-fetch:', spaceName);
    }
  }, [spaceName, hasCustomText, currentTextDirections]);

  // Check for changes
  useEffect(() => {
    setHasChanges(textDirections !== currentTextDirections);
  }, [textDirections, currentTextDirections]);

  // Handle text change - update both SSML and display text
  const handleTextChange = (e) => {
    const newText = e.target.value;
    setDisplayText(newText);
    setTextDirections(newText);
    // Mark as custom text if user manually edits
    if (newText !== currentTextDirections) {
      setHasCustomText(true);
    }
  };

  // Handle directions fetched from MappedIn
  const handleDirectionsFetched = (directionsText, result) => {
    // directionsText already includes SSML formatting
    setTextDirections(directionsText);
    setDisplayText(directionsText); // Show SSML tags in textarea
    setHasCustomText(false); // Mark as auto-generated
    setUpdateResult({
      type: 'success',
      message: `Successfully fetched directions for "${spaceName}" from MappedIn`,
      data: {
        totalDistance: result.totalDistance,
        estimatedTime: result.estimatedTime,
        instructionCount: result.instructions?.length || 0
      }
    });

    // Clear result after 10 seconds
    setTimeout(() => {
      setUpdateResult(null);
    }, 10000);
  };

  // Show confirmation dialog
  const handleUpdateClick = () => {
    if (!hasChanges) {
      return;
    }
    setShowConfirmDialog(true);
  };

  // Update text directions and regenerate videos
  const updateTextDirectionsAndRegenerateVideos = async () => {
    if (!userId || !locationId || !textDirections.trim()) {
      return;
    }

    try {
      setIsUpdating(true);
      setUpdateResult(null);
      setShowConfirmDialog(false);

      const response = await axios.put(
        `/api/wayfinder/locations/${locationId}/update-text-and-regenerate`,
        {
          textDirections: textDirections.trim(),
          resolution: '4k', // Default to 4K resolution,
          currentLanguage // Pass current language for video regeneration
        }
      );

      if (response.data.success) {
        setUpdateResult({
          type: 'success',
          message: 'Text directions updated and video regeneration initiated',
          data: response.data.data
        });

        setTimeout(() => {
          fetchVideoStatus();
        }, 1000);

        // Notify parent component
        if (onTextDirectionsUpdate) {
          onTextDirectionsUpdate(textDirections.trim(), response.data.data);
        }

        setHasChanges(false);
      } else {
        setUpdateResult({
          type: 'error',
          message: response.data.message || 'Failed to update text directions'
        });
      }
    } catch (error) {
      console.error('Error updating text directions:', error);
      setUpdateResult({
        type: 'error',
        message: error.response?.data?.message || 'Failed to update text directions'
      });
    } finally {
      setIsUpdating(false);
      // Clear result after 10 seconds
      setTimeout(() => {
        setUpdateResult(null);
      }, 10000);
    }
  };

  // Cancel update
  const cancelUpdate = () => {
    setShowConfirmDialog(false);
  };

  // Reset to original text
  const resetText = () => {
    setTextDirections(currentTextDirections || '');
    setDisplayText(currentTextDirections || ''); // Show SSML tags
    setHasChanges(false);
  };

  return (
    <div className="text-direction-manager">
      <div className="text-input-section">
        <label className="text-label">
          Text Directions:
        </label>
        <textarea
          value={displayText}
          onChange={handleTextChange}
          className="text-input"
          placeholder='Enter text directions for this location... (You can include SSML tags like <break time="1.0s" /> for pauses)'
          rows={6}
          disabled={isUpdating}
        />
        {/* {textDirections.includes('<break time=') && (
          <div className="ssml-indicator">
            ℹ️ This text includes SSML formatting for video narration. The &lt;break time="X.Xs" /&gt; tags create pauses in the avatar's speech.
          </div>
        )} */}

        <div className="text-actions">
          <MappedInDirectionsButton
            spaceName={spaceName}
            onDirectionsFetched={handleDirectionsFetched}
            disabled={isUpdating}
            userId={userId}
            locationId={locationId}
            useAIEnhancement={true}
          />

          {hasChanges && (
            <>
              <button
                className="action-btn update-btn"
                onClick={handleUpdateClick}
                disabled={isUpdating || !textDirections.trim()}
              >
                {isUpdating ? '⏳ Updating...' : '🔄 Update & Regenerate Videos'}
              </button>

              <button
                className="action-btn reset-btn"
                onClick={resetText}
                disabled={isUpdating}
              >
                ↩️ Reset
              </button>
            </>
          )}
          {!hasChanges && textDirections && (
            <div className="no-changes-message">
              ✅ Text directions are up to date
              {hasCustomText && <span className="custom-indicator"> (Custom text)</span>}
              {!hasCustomText && <span className="auto-indicator"> (Auto-generated from MappedIn)</span>}
            </div>
          )}
        </div>
      </div>

      {/* Update Result */}
      {updateResult && (
        <div className={`update-result ${updateResult.type}`}>
          <div className="result-message">
            {updateResult.message}
          </div>
          {updateResult.data && updateResult.type === 'success' && (
            <div className="result-details">
              {/* MappedIn fetch results */}
              {updateResult.data.totalDistance !== undefined && (
                <>
                  <div className="detail-item">
                    <span className="detail-label">Total Distance:</span>
                    <span className="detail-value">{Math.round(updateResult.data.totalDistance)} m</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Estimated Time:</span>
                    <span className="detail-value">{updateResult.data.estimatedTime}s</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Instructions:</span>
                    <span className="detail-value">{updateResult.data.instructionCount} steps</span>
                  </div>
                </>
              )}
              {/* Video regeneration results */}
              {updateResult.data.totalVideosRegenerated !== undefined && (
                <>
                  <div className="detail-item">
                    <span className="detail-label">Videos to regenerate:</span>
                    <span className="detail-value">{updateResult.data.totalVideosRegenerated}</span>
                  </div>
                  {updateResult.data.regenerationResults && updateResult.data.regenerationResults.length > 0 && (
                    <div className="regeneration-list">
                      <div className="list-title">Regeneration Status:</div>
                      {updateResult.data.regenerationResults.map((result, index) => (
                        <div key={index} className="regeneration-item">
                          <span className="language-code">{result.language.toUpperCase()}</span>
                          <span className={`status-indicator ${result.status}`}>
                            {result.status === 'initiated' ? '🎬' : '❌'} {result.status}
                          </span>
                          {result.error && (
                            <span className="error-text">{result.error}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="confirmation-overlay">
          <div className="confirmation-dialog">
            <div className="dialog-header">
              <h3>🔄 Update Text Directions & Regenerate Videos?</h3>
            </div>
            <div className="dialog-content">
              <div className="text-comparison">
                <div className="text-section">
                  <div className="text-section-title">Current Text:</div>
                  <div className="text-preview current">
                    {currentTextDirections || 'No text directions set'}
                  </div>
                </div>
                <div className="text-section">
                  <div className="text-section-title">New Text:</div>
                  <div className="text-preview new">
                    {textDirections}
                  </div>
                </div>
              </div>
              <div className="warning-info">
                <div className="warning-item">
                  <span className="warning-icon">⚠️</span>
                  <span>This will update text directions and regenerate ALL existing videos</span>
                </div>
                <div className="warning-item">
                  <span className="warning-icon">🗑️</span>
                  <span>Old videos will be removed from AWS storage</span>
                </div>
                <div className="warning-item">
                  <span className="warning-icon">🎥</span>
                  <span>New 4K videos will be generated for all languages</span>
                </div>
                <div className="warning-item">
                  <span className="warning-icon">⏱️</span>
                  <span>Process may take several minutes to complete</span>
                </div>
              </div>
            </div>

            <div className="dialog-actions">
              <button
                className="btn btn-primary"
                onClick={updateTextDirectionsAndRegenerateVideos}
                disabled={isUpdating}
              >
                🚀 Yes, Update & Regenerate
              </button>
              <button
                className="btn btn-cancel"
                onClick={cancelUpdate}
                disabled={isUpdating}
              >
                ❌ Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .text-direction-manager {
          margin: 20px 0;
          padding: 20px;
          background: #f8f9fa;
          border-radius: 8px;
          border: 1px solid #dee2e6;
        }

        .text-input-section {
          margin-bottom: 15px;
        }

        .text-label {
          display: block;
          font-weight: 600;
          color: #333;
          margin-bottom: 8px;
          font-size: 14px;
        }

        .text-input {
          width: 100%;
          padding: 12px;
          border: 1px solid #dee2e6;
          border-radius: 6px;
          font-size: 14px;
          font-family: inherit;
          resize: vertical;
          min-height: 100px;
          transition: border-color 0.3s ease;
        }

        .text-input:focus {
          outline: none;
          border-color: #007bff;
          box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
        }

        .text-input:disabled {
          background: #f8f9fa;
          cursor: not-allowed;
        }

        .ssml-indicator {
          margin-top: 8px;
          padding: 8px 12px;
          background: #e7f3ff;
          color: #004085;
          border-radius: 4px;
          font-size: 13px;
          border: 1px solid #b8daff;
        }

        .text-actions {
          display: flex;
          gap: 10px;
          align-items: center;
          margin-top: 10px;
        }

        .action-btn {
          padding: 8px 16px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: all 0.3s ease;
        }

        .action-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .update-btn {
          background: ${buttonColor || '#007bff'};
          color: white;
        }

        .update-btn:hover:not(:disabled) {
          background: ${buttonColor ? `${buttonColor}dd` : '#0056b3'};
          opacity: 0.85;
        }

        .reset-btn {
          background: #6c757d;
          color: white;
        }

        .reset-btn:hover:not(:disabled) {
          background: #545b62;
        }

        .no-changes-message {
          color: #28a745;
          font-size: 14px;
          font-weight: 500;
        }

        .custom-indicator {
          color: #007bff;
          font-size: 12px;
        }

        .auto-indicator {
          color: #6c757d;
          font-size: 12px;
        }

        .update-result {
          margin-top: 15px;
          padding: 15px;
          border-radius: 6px;
          border-left: 4px solid;
        }

        .update-result.success {
          background: #d4edda;
          border-color: #28a745;
          color: #155724;
        }

        .update-result.error {
          background: #f8d7da;
          border-color: #dc3545;
          color: #721c24;
        }

        .result-message {
          font-weight: 600;
          margin-bottom: 10px;
        }

        .result-details {
          font-size: 14px;
        }

        .detail-item {
          display: flex;
          justify-content: space-between;
          margin-bottom: 8px;
        }

        .detail-label {
          font-weight: 500;
        }

        .detail-value {
          font-weight: 600;
        }

        .regeneration-list {
          margin-top: 10px;
          padding: 10px;
          background: rgba(255, 255, 255, 0.5);
          border-radius: 4px;
        }

        .list-title {
          font-weight: 600;
          margin-bottom: 8px;
        }

        .regeneration-item {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 4px;
          font-size: 13px;
        }

        .language-code {
          font-weight: 600;
          min-width: 30px;
        }

        .status-indicator {
          font-size: 12px;
        }

        .status-indicator.initiated {
          color: #007bff;
        }

        .status-indicator.failed {
          color: #dc3545;
        }

        .error-text {
          color: #dc3545;
          font-size: 12px;
        }

        .confirmation-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .confirmation-dialog {
          background: white;
          border-radius: 10px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.3);
          max-width: 600px;
          width: 90%;
          max-height: 90vh;
          overflow-y: auto;
        }

        .dialog-header {
          padding: 20px 20px 0 20px;
          border-bottom: 1px solid #dee2e6;
        }

        .dialog-header h3 {
          margin: 0 0 15px 0;
          color: #333;
          font-size: 18px;
        }

        .dialog-content {
          padding: 20px;
        }

        .text-comparison {
          margin-bottom: 20px;
        }

        .text-section {
          margin-bottom: 15px;
        }

        .text-section-title {
          font-weight: 600;
          color: #333;
          margin-bottom: 5px;
          font-size: 14px;
        }

        .text-preview {
          padding: 10px;
          border-radius: 4px;
          font-size: 14px;
          line-height: 1.4;
          max-height: 100px;
          overflow-y: auto;
        }

        .text-preview.current {
          background: #fff3cd;
          border: 1px solid #ffeaa7;
          color: #856404;
        }

        .text-preview.new {
          background: #d1ecf1;
          border: 1px solid #bee5eb;
          color: #0c5460;
        }

        .warning-info {
          background: #fff3cd;
          padding: 15px;
          border-radius: 6px;
          border: 1px solid #ffeaa7;
        }

        .warning-item {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 8px;
          font-size: 14px;
          color: #856404;
        }

        .warning-item:last-child {
          margin-bottom: 0;
        }

        .warning-icon {
          font-size: 16px;
          min-width: 20px;
        }

        .dialog-actions {
          padding: 0 20px 20px 20px;
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }

        .btn {
          padding: 10px 20px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.3s ease;
          flex: 1;
          min-width: 120px;
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-primary {
          background: ${buttonColor || '#007bff'};
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          background: ${buttonColor ? `${buttonColor}dd` : '#0056b3'};
          opacity: 0.85;
        }

        .btn-cancel {
          background: #6c757d;
          color: white;
        }

        .btn-cancel:hover:not(:disabled) {
          background: #545b62;
        }

        @media (max-width: 768px) {
          .text-actions {
            flex-direction: column;
            align-items: flex-start;
          }

          .action-btn {
            width: 100%;
          }

          .dialog-actions {
            flex-direction: column;
          }

          .btn {
            flex: none;
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default TextDirectionManager;